/**
 * Selectors for working with a draft sheet to edit columns for said sheet.
 */

/** @typedef {{exportTemplate: import('../state').ExportTemplateState}} RootState */

/**
 * Returns the current draft sheet, or null if no draft is currently active.
 * @param {RootState} state
 */
export const getDraftSheet = state => state.exportTemplate.edit.draftSheet;

/**
 * Returns the metadata ID of the current draft sheet, or null if no draft is
 * currently active.
 * @param {RootState} state
 */
export const getDraftSheetId = state => state.exportTemplate.edit.draftSheetId;

/**
 * Returns the column order of the current draft sheet, or null if no draft is
 * currently active.
 * @param {RootState} state
 */
export const getDraftSheetColumnOrder = state =>
  getDraftSheet(state)?.columnOrder ?? null;

/**
 * Returns the column map of the current draft sheet, or null if no draft is
 * currently active.
 * @param {RootState} state
 */
export const getDraftSheetColumnMap = state =>
  getDraftSheet(state)?.columnMap ?? null;

/**
 * Returns the overriden name > default name of the current draft sheet or null
 * if no draft is currently active.
 * @param {RootState} state
 */
export const getDraftSheetName = state => {
  const s = getDraftSheet(state);
  if (!s) return null;
  return s.overridenName ?? s.defaultName ?? null;
};

/**
 * Returns an EditingColumn for the given columnId or null if no draft is
 * currently active.
 * @param {RootState} state
 * @param {string} columnId
 */
export const getDraftColumn = (state, columnId) =>
  getDraftSheet(state)?.columnMap[columnId] ?? null;

/**
 * Builds a deterministic JSON string from the editable fields of a single sheet.
 * @param {import('../state').EditingSheet} sheet
 * @returns {string}
 */
const createSheetFingerprint = sheet => {
  return JSON.stringify({
    enabled: sheet.enabled,
    name: sheet.overridenName,
    columns: sheet.columnOrder.map(colId => {
      const col = sheet.columnMap[colId];
      return { id: colId, enabled: col.enabled, name: col.overridenName };
    }),
  });
};

/**
 * Returns true if the draft sheet has been modified compared to the source sheet.
 * Returns false if there is no active draft.
 * @param {RootState} state
 * @returns {boolean}
 */
export const isDraftDirty = state => {
  const draftSheet = getDraftSheet(state);
  const draftSheetId = getDraftSheetId(state);
  if (!draftSheet || !draftSheetId) return false;
  const sourceSheet = state.exportTemplate.edit.sheetMap[draftSheetId];
  if (!sourceSheet) return false;
  return (
    createSheetFingerprint(draftSheet) !== createSheetFingerprint(sourceSheet)
  );
};
